import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiAlertCircle, FiCheckCircle, FiMail, FiPhone } from "react-icons/fi";
import { contact } from "../content/contact";
import { team } from "../content/team";
import "../styles/contact.css";

/** Lead `service` codes. Unchanged data model: ai | sap | finance | logistics. */
export type Topic = "finance" | "logistics" | "ai" | "sap";
export const TOPICS: Topic[] = ["finance", "logistics", "ai", "sap"];
export const TOPIC_EVENT = "dux:topic";

const isTopic = (v: unknown): v is Topic => TOPICS.includes(v as Topic);

/** Short chip labels. (The admin CRM labels the same codes with quoteForm.option*.) */
const topicKey: Record<Topic, string> = {
  finance: "quoteForm.topicFinance",
  logistics: "quoteForm.topicLogistics",
  ai: "quoteForm.topicAi",
  sap: "quoteForm.topicSap",
};

/**
 * Preselect a topic in the contact form and bring the form into view.
 * Used by the Services "Discuss" links (spec c.7 §4).
 */
export const requestTopic = (topic: Topic) => {
  window.dispatchEvent(new CustomEvent<Topic>(TOPIC_EVENT, { detail: topic }));
  // Below 1024 the intro stacks above the form, so jumping to the section would
  // leave the preselected topic below the fold: go to the form panel instead.
  const section = document.getElementById("contact");
  if (!section) return;
  const wide = window.matchMedia("(min-width: 1024px)").matches;
  const target = (wide ? section : section.querySelector<HTMLElement>(".contact-panel")) ?? section;
  try {
    window.history.pushState(null, "", "#contact");
  } catch {
    /* ignore (sandboxed history) */
  }
  // "auto" defers to the CSS scroll-behavior, which is smooth only without reduced motion.
  target.scrollIntoView({ block: "start" });
  const radio = document.getElementById(`contact-topic-${topic}`) as HTMLInputElement | null;
  radio?.focus({ preventScroll: true });
};

type Values = {
  name: string;
  email: string;
  company: string;
  service: Topic | "";
  message: string;
  /** Honeypot. Never written to Firestore. */
  website: string;
};
type Field = "name" | "email";
type Errors = Partial<Record<Field, string>>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/** addDoc (Firestore Lite) waits for the server ack; give up visibly after this. */
const SUBMIT_TIMEOUT_MS = 15000;

const initialTopic = (): Topic | "" => {
  try {
    const q = new URLSearchParams(window.location.search).get("topic");
    return isTopic(q) ? q : "";
  } catch {
    return "";
  }
};

/** Loads Firestore and the app's `db` on demand. Never called at module scope. */
const loadFirestore = () =>
  Promise.all([import("firebase/firestore/lite"), import("../firebaseDb")]);

const Contact: React.FC = () => {
  const { t } = useTranslation();
  const uid = "contact";
  const [values, setValues] = useState<Values>(() => ({
    name: "",
    email: "",
    company: "",
    service: initialTopic(),
    message: "",
    website: "",
  }));
  const [touched, setTouched] = useState<Record<Field, boolean>>({ name: false, email: false });
  const [showSummary, setShowSummary] = useState(false);
  /** Bumped on every failed submit so focus moves to the summary after it renders. */
  const [summaryTick, setSummaryTick] = useState(0);
  const [status, setStatus] = useState<"idle" | "sending" | "error" | "sent">("idle");
  const [sentTo, setSentTo] = useState<{ name: string; email: string } | null>(null);
  const [panelMinHeight, setPanelMinHeight] = useState<number | undefined>();

  const panelRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLHeadingElement>(null);
  const prefetched = useRef(false);

  const validate = useCallback(
    (v: Values): Errors => {
      const e: Errors = {};
      if (!v.name.trim()) e.name = t("quoteForm.errName");
      if (!EMAIL_RE.test(v.email.trim())) e.email = t("quoteForm.errEmail");
      return e;
    },
    [t]
  );
  const errors = validate(values);
  const visibleError = (f: Field) => (touched[f] ? errors[f] : undefined);

  // Topic preselect from the Services rows.
  useEffect(() => {
    const onTopic = (e: Event) => {
      const topic = (e as CustomEvent).detail;
      if (isTopic(topic)) setValues((v) => ({ ...v, service: topic }));
    };
    window.addEventListener(TOPIC_EVENT, onTopic);
    return () => window.removeEventListener(TOPIC_EVENT, onTopic);
  }, []);

  useEffect(() => {
    if (status === "sent") successRef.current?.focus();
  }, [status]);

  useEffect(() => {
    if (summaryTick) summaryRef.current?.focus();
  }, [summaryTick]);

  useEffect(() => {
    if (showSummary && Object.keys(errors).length === 0) setShowSummary(false);
  }, [showSummary, errors]);

  /** Warm the Firestore chunk on first focus. No network request to Firestore happens here. */
  const prefetch = () => {
    if (prefetched.current) return;
    prefetched.current = true;
    loadFirestore().catch(() => {
      prefetched.current = false;
    });
  };

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
  };

  const onBlur = (f: Field) => () => setTouched((s) => ({ ...s, [f]: true }));

  const focusField = (f: Field) => (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById(`${uid}-${f}`)?.focus();
  };

  const showSuccess = (name: string, email: string) => {
    if (panelRef.current) setPanelMinHeight(panelRef.current.offsetHeight);
    setSentTo({ name, email });
    setStatus("sent");
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === "sending") return;
    setTouched({ name: true, email: true });
    const errs = validate(values);
    if (Object.keys(errs).length) {
      setShowSummary(true);
      setSummaryTick((n) => n + 1);
      return;
    }
    setShowSummary(false);

    const name = values.name.trim();
    const email = values.email.trim();

    // Honeypot filled: pretend success, write nothing.
    if (values.website) {
      showSuccess(name, email);
      return;
    }

    setStatus("sending");
    try {
      const [{ collection, addDoc, serverTimestamp }, { db }] = await loadFirestore();
      // Same document shape as the previous QuoteForm (firestore.rules + admin CRM depend on it).
      const write = addDoc(collection(db, "leads"), {
        name,
        company: values.company.trim(),
        email,
        service: values.service,
        message: values.message.trim(),
        source: "quote_form",
        status: "unqualified",
        pipelineStage: "new",
        assignedTo: null,
        createdAt: serverTimestamp(),
      });
      await Promise.race([
        write,
        new Promise((_, reject) =>
          window.setTimeout(() => reject(new Error("timeout")), SUBMIT_TIMEOUT_MS)
        ),
      ]);
      showSuccess(name, email);
    } catch (err) {
      console.error("Lead submit failed:", err);
      setStatus("error");
    }
  };

  const raul = team.find((m) => m.key === "raul");
  const sending = status === "sending";
  const errorList = (Object.keys(errors) as Field[]).filter((f) => errors[f]);

  const describedBy = (f: Field, hint?: string) =>
    [hint, visibleError(f) ? `${uid}-${f}-error` : ""].filter(Boolean).join(" ") || undefined;

  const fieldError = (f: Field) =>
    visibleError(f) ? (
      <p className="field-error" id={`${uid}-${f}-error`}>
        <FiAlertCircle aria-hidden="true" focusable="false" />
        <span>
          <span className="visually-hidden">{t("quoteForm.errorPrefix")} </span>
          {visibleError(f)}
        </span>
      </p>
    ) : null;

  return (
    <section id="contact" className="contact section surface-2" aria-labelledby="contact-heading">
      <span id="quote" aria-hidden="true" />
      <div className="container">
        <div className="contact-grid">
          <div className="contact-intro">
            <p className="t-label">{t("quoteForm.label")}</p>
            <h2 id="contact-heading">{t("quoteForm.title")}</h2>
            <p className="t-lead">{t("quoteForm.lead")}</p>

            <address className="contact-card">
              <p className="t-small contact-card-label">{t("quoteForm.direct")}</p>
              <div className="contact-person">
                {raul && (
                  <span className="monogram monogram--48" aria-hidden="true">
                    {raul.initials}
                  </span>
                )}
                <div>
                  <p className="contact-name">{contact.name}</p>
                  <p className="t-small">{t(contact.roleKey)}</p>
                </div>
              </div>
              <ul className="contact-links">
                <li>
                  <a href={`mailto:${contact.email}`}>
                    <FiMail aria-hidden="true" focusable="false" />
                    <span className="visually-hidden">{t("quoteForm.emailLabel")}: </span>
                    {contact.email}
                  </a>
                </li>
                <li>
                  <a href={`tel:${contact.phone}`} className="contact-phone">
                    <FiPhone aria-hidden="true" focusable="false" />
                    <span className="visually-hidden">{t("quoteForm.phoneLabel")}: </span>
                    {contact.phoneDisplay}
                  </a>
                </li>
                {contact.whatsapp && (
                  <li>
                    <a href={`https://wa.me/${contact.whatsapp.replace(/\D/g, "")}`}>
                      WhatsApp
                    </a>
                  </li>
                )}
              </ul>
              {contact.city && <p className="t-small">{contact.city}</p>}
            </address>
          </div>

          <div
            className="panel contact-panel"
            ref={panelRef}
            style={panelMinHeight ? { minHeight: panelMinHeight } : undefined}
          >
            {status === "sent" && sentTo ? (
              <div className="contact-success" aria-live="polite">
                <FiCheckCircle className="contact-success-icon" aria-hidden="true" focusable="false" />
                <h3 ref={successRef} tabIndex={-1}>
                  {t("quoteForm.successTitle", { name: sentTo.name })}
                </h3>
                <p>
                  {t("quoteForm.successBody", {
                    contactName: contact.name,
                    email: sentTo.email,
                  })}
                </p>
              </div>
            ) : (
              <form
                className="contact-form"
                onSubmit={submit}
                onFocus={prefetch}
                noValidate
                aria-labelledby="contact-heading"
              >
                {showSummary && errorList.length > 0 && (
                  <div
                    className="notice notice--error contact-summary"
                    role="alert"
                    tabIndex={-1}
                    ref={summaryRef}
                  >
                    <FiAlertCircle aria-hidden="true" focusable="false" />
                    <div>
                      <p className="contact-summary-title">{t("quoteForm.summary")}</p>
                      <ul>
                        {errorList.map((f) => (
                          <li key={f}>
                            <a href={`#${uid}-${f}`} onClick={focusField(f)}>
                              {errors[f]}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                <div className="contact-row">
                  <div className="field">
                    <label className="field-label" htmlFor={`${uid}-name`}>
                      {t("quoteForm.name")}
                    </label>
                    <input
                      id={`${uid}-name`}
                      name="name"
                      className="input"
                      autoComplete="name"
                      value={values.name}
                      onChange={onChange}
                      onBlur={onBlur("name")}
                      aria-required="true"
                      aria-invalid={visibleError("name") ? true : undefined}
                      aria-describedby={describedBy("name")}
                    />
                    {fieldError("name")}
                  </div>
                  <div className="field">
                    <label className="field-label" htmlFor={`${uid}-email`}>
                      {t("quoteForm.email")}
                    </label>
                    <input
                      id={`${uid}-email`}
                      name="email"
                      type="email"
                      inputMode="email"
                      className="input"
                      autoComplete="email"
                      spellCheck={false}
                      value={values.email}
                      onChange={onChange}
                      onBlur={onBlur("email")}
                      aria-required="true"
                      aria-invalid={visibleError("email") ? true : undefined}
                      aria-describedby={describedBy("email")}
                    />
                    {fieldError("email")}
                  </div>
                </div>

                <div className="field">
                  <label className="field-label" htmlFor={`${uid}-company`}>
                    {t("quoteForm.company")}{" "}
                    <span className="field-optional">{t("quoteForm.optional")}</span>
                  </label>
                  <input
                    id={`${uid}-company`}
                    name="company"
                    className="input"
                    autoComplete="organization"
                    value={values.company}
                    onChange={onChange}
                  />
                </div>

                <fieldset className="field topics">
                  <legend className="field-label">
                    {t("quoteForm.topic")}{" "}
                    <span className="field-optional">{t("quoteForm.optional")}</span>
                  </legend>
                  <div className="topic-list">
                    {TOPICS.map((topic) => (
                      <label key={topic} className="topic">
                        <input
                          type="radio"
                          id={`contact-topic-${topic}`}
                          name="service"
                          value={topic}
                          checked={values.service === topic}
                          onChange={() => setValues((v) => ({ ...v, service: topic }))}
                        />
                        <span>{t(topicKey[topic])}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div className="field">
                  <label className="field-label" htmlFor={`${uid}-message`}>
                    {t("quoteForm.message")}{" "}
                    <span className="field-optional">{t("quoteForm.optional")}</span>
                  </label>
                  <textarea
                    id={`${uid}-message`}
                    name="message"
                    className="input"
                    rows={4}
                    value={values.message}
                    onChange={onChange}
                    placeholder={t("quoteForm.messagePlaceholder")}
                  />
                </div>

                <div className="contact-hp" aria-hidden="true">
                  <label htmlFor={`${uid}-website`}>{t("quoteForm.honeypot")}</label>
                  <input
                    id={`${uid}-website`}
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={values.website}
                    onChange={onChange}
                  />
                </div>

                {status === "error" && (
                  <div className="notice notice--error" role="alert">
                    <FiAlertCircle aria-hidden="true" focusable="false" />
                    <p>
                      {t("quoteForm.networkError")}{" "}
                      <a href={`mailto:${contact.email}`}>{contact.email}</a>
                    </p>
                  </div>
                )}

                <div className="contact-submit">
                  <button
                    type="submit"
                    className="btn btn--primary btn--lg"
                    disabled={sending}
                    aria-busy={sending || undefined}
                  >
                    {sending ? t("quoteForm.sending") : t("quoteForm.submit")}
                  </button>
                  <p className="t-small contact-privacy">
                    {t("quoteForm.privacy")}{" "}
                    <Link to="/privacy">{t("quoteForm.privacyLink")}</Link>
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
